import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Layout } from '@/ui/Layout';
import { Button } from '@/ui/Button';
import { CoursePageDiscussion } from '@/component/Discussions/CoursePageDiscussion';
import { useLearningJourney } from '@/store/v2/journeys/useLearningJourney';
import { useJourneyNavigate } from '@/administration/pages/Journey/utils/useJourneyNavigate';
import { slugify } from '@/utils/misc';
import { CoursePageContext } from '@/pages/learning/CourseDetails/CoursePageContext';
import { useHasSpacePermission } from '@/store/spaces';
import { SpacePermission } from '@/types/learning/learning-catalog';
import { useLearningJourneyDiscussionTeamsQueryRemote } from '@/store/v2';
import { Teams } from '@/administration/pages/ProductEditor/component/CourseTeams';
import { useCurrentUser } from '@/store/currentUser';

const HeroStyled = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 34px;
`;

const TitleStyled = styled.div`
  ${({ theme }) => theme.typography.title2};
  color: ${({ theme }) => theme.colors.neutral.onBackground};
  padding-left: 16px;
`;

export const JourneyEditViewDiscussions = (): JSX.Element | null => {
  const { navigateToLearnerView, navigate } = useJourneyNavigate();

  const { user } = useCurrentUser();
  const { journeyId } = useParams<{ journeyId: string }>();

  const { data } = useLearningJourneyDiscussionTeamsQueryRemote({ variables: { id: Number(journeyId) }, skip: !journeyId });

  const teamsMap = useMemo(() => {
    const newTeams = new Map();

    if (!data?.learningJourney?.sessions?.[0]?.discussionTeams) return null;

    data?.learningJourney?.sessions?.[0]?.discussionTeams?.forEach((team, index) => {
      newTeams.set(
        index,
        team.members.map((member) => ({ id: member.userId })),
      );
    });

    return newTeams;
  }, [data]) as Teams;

  // `Teams` is the product-teams shape and has no slot for a name, so the names travel alongside
  // it on the same keys rather than the map being widened for one of its two callers.
  const teamNames = useMemo(() => {
    const names = new Map<number, string>();

    data?.learningJourney?.sessions?.[0]?.discussionTeams?.forEach((team, index) => {
      names.set(index, team.name);
    });

    return names;
  }, [data]);

  const userTeamSectionName = useMemo(() => {
    const discussionTeam = data?.learningJourney?.sessions?.[0]?.discussionTeams?.find((team) =>
      team.members.find((member) => member.userId === user?.id),
    );
    if (!discussionTeam) return undefined;

    return discussionTeam.name.replace(' ', '-').toLowerCase(); // TODO: to be moved to backed
  }, [user?.id, data]);

  const { journey, loading: journeyLoading } = useLearningJourney({
    id: Number(journeyId),
  });

  const { data: isSpaceAdmin } = useHasSpacePermission(journey?.spaceId || 0, SpacePermission.HasWriteAccessToLearningContent);

  if (journeyLoading || !journey?.discussionForumSectionPath) return null;

  const handleNavigateBack = () => {
    if (!journeyId || !journey.title) {
      navigate(-1);
    }

    navigateToLearnerView({
      journeyId: journey.id,
      journeyTitle: slugify(journey.title),
    });
  };

  return (
    <>
      <Layout.ContentSection marginTop>
        <HeroStyled>
          <Button variant="ghost" icon="arrow-left" onClick={handleNavigateBack}>
            Back
          </Button>
          <TitleStyled>Discussion Channels</TitleStyled>
        </HeroStyled>
      </Layout.ContentSection>
      <Layout.ContentSection marginTop marginBottom>
        <div>
          <CoursePageContext.Provider value={{ spaceId: journey?.spaceId, isAdmin: isSpaceAdmin }}>
            <CoursePageDiscussion
              path={journey.discussionForumSectionPath?.split('/').slice(1) || []}
              spaceId={journey.spaceId}
              enableGeneralChannel
              teams={teamsMap}
              teamNames={teamNames}
              userTeamSectionName={userTeamSectionName}
              isJourney
              journeyId={journey?.id as number}
              journeyTitle={journey?.title}
            />
          </CoursePageContext.Provider>
        </div>
      </Layout.ContentSection>
    </>
  );
};
